import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "ui/button";
import { StateNvr, createNvr } from "lib/actions";
import React, { useContext, useState } from "react";
import OTPInput from "ui/OTPInput/index";
import { AuthContext } from "context/AuthContext";

export default function Form({
  orgId,
  siteId,
}: {
  orgId: string;
  siteId: string;
}) {
  const [macAddress, setMacAddress] = useState("");
  const [serialNumber, setSerialNumber] = useState("");

  const { user } = useContext(AuthContext);
  interface formDataType {
    [key: string]: FormDataEntryValue;
  }
  const responseBody: formDataType = {};
  const navigate = useNavigate();

  const [responseData, setResponseData] = useState<StateNvr>();
  const [btnSubmitDisabled, setBtnSubmitDisabled] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    formData.forEach(
      (value, property: string) => (responseBody[property] = value),
    );

    try {
      setBtnSubmitDisabled(true);
      const response = await createNvr(orgId, siteId, formData);
      setBtnSubmitDisabled(false);
      setResponseData(response);
      if (response.strReturn) {
        const idCreated = response.strReturn;
        // navigate(`/dashboard/customers/${orgId}/sites/${siteId}/nvrs/${idCreated}/cameras/create`);
      }
    } catch (error) {
      console.error(error);
    }
  }

  if (responseData?.strReturn) {
    const objReturn = responseData.strReturn;
    return (
      <>
        <div className="p-4 md:p-6">
          <h2 className="text-lg font-medium mb-5">Add NVR to site</h2>
          <div className="bg-gray-100 rounded-lg p-4 md:p-6 grid grid-cols-2 gap-4">
            <div>
              NVR Found
              <CheckCircleIcon className="inline-block ml-3 w-5 md:w-6" />
            </div>
            <div />
            <p className="text-slate-500">NVR Name</p>
            <p>{objReturn.nvr.name}</p>
            <p className="text-slate-500">Model</p>
            <p>{objReturn.nvr.model}</p>
            <p className="text-slate-500">MAC Address</p>
            <p>{objReturn.nvr.macAddress}</p>
            <p className="text-slate-500">Serial Number</p>
            <p>{objReturn.nvr.sn}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/dashboard/customers"
            className="flex h-10 items-center rounded-lg bg-gray-100 px-4 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancel
          </Link>
          <Link
            to={`/dashboard/customers/${orgId}/sites/${siteId}/nvrs/${objReturn.nvr.id}/cameras/create`}
            className="flex h-10 items-center rounded-lg bg-blue-500 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          >
            Search Cameras
          </Link>
        </div>
      </>
    );
  } else {
    return (
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
        <div className="rounded-md">
          <h2 className="text-lg font-medium mb-5">Add NVR to site</h2>

          {/* Site Name */}
          <div className="mb-4">
            <label htmlFor="macAddress" className="mb-2 block text-sm">
              Last 6 digits of MAC Address
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <OTPInput
                  autoFocus
                  length={6}
                  className="otpContainer"
                  inputClassName="otpInput w-12 h-12 mr-3 text-base text-center rounded-md border border-gray-200"
                  isMacAddressInput
                  onChangeOTP={(otp) =>
                    setMacAddress(otp.replace(/(..)/g, "$1:").slice(0, -1))
                  }
                />
                <input
                  id="macAddress"
                  name="macAddress"
                  type="hidden"
                  placeholder="Last 6 digits of MAC Address"
                  className="block w-full rounded-md border border-gray-200 py-2 text-sm outline-2 placeholder:text-gray-500"
                  aria-describedby="macAddress-error"
                  value={macAddress}
                />
              </div>
              <div id="macAddress-error" aria-live="polite" aria-atomic="true">
                {responseData?.errors?.macAddress &&
                  responseData.errors.macAddress.map((error: string) => (
                    <p className="mt-2 text-sm text-red-500" key={error}>
                      {error}
                    </p>
                  ))}
              </div>
            </div>
          </div>

          {/* Serial Number */}
          <div className="mb-4">
            <label htmlFor="serialNumber" className="mb-2 block text-sm">
              Last 6 characters of Serial Number
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <OTPInput
                  autoFocus
                  length={6}
                  className="otpContainer"
                  inputClassName="otpInput w-12 h-12 mr-3 text-base text-center rounded-md border border-gray-200"
                  onChangeOTP={(otp) => setSerialNumber(otp)}
                />
                <input
                  id="serialNumber"
                  name="serialNumber"
                  type="hidden"
                  placeholder="Last 6 characters of Serial Number"
                  className="block w-full rounded-md border border-gray-200 py-2 text-sm outline-2 placeholder:text-gray-500"
                  aria-describedby="serialNumber-error"
                  value={serialNumber}
                />
              </div>
              <div
                id="serialNumber-error"
                aria-live="polite"
                aria-atomic="true"
              >
                {responseData?.errors?.serialNumber &&
                  responseData.errors.serialNumber.map((error: string) => (
                    <p className="mt-2 text-sm text-red-500" key={error}>
                      {error}
                    </p>
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/dashboard/customers"
            className="p-4 flex items-center rounded-lg bg-gray-100 px-4 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancel
          </Link>
          <Button type="submit" aria-disabled={btnSubmitDisabled}>
            Continue
          </Button>
        </div>
        {responseData?.message && (
          <div
            className="p-4 mb-4 mt-12 text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 flex items-center space-x-1"
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
            <p className="text-sm text-red-500">{responseData.message}</p>
          </div>
        )}
      </form>
    );
  }
}
